import {
  TableCellType,
  TableHeader,
} from "../../../components/custom-table/table";
import { PharmaStpFileModel } from "../../../data-model/nycbsPharmaOrder";
import {
  TableCellMultiline,
  TableCellWithBadge,
} from "../../../data-model/tableCells";
import { OncologySubmissionTableDataModel } from "../../../data-model/tablesData";
import { CmmStpStatus } from "../../../enums/cmmStpStatus";
import {
  SecondStpStatus,
  SecondStpStatusLabels,
} from "../../../enums/secondStpStatus";
import { OncologySubmissionTableColumnKeys } from "../../../enums/tableColumnKeys";
import { mapPharmaStpFileExtendedRowFields } from "../../pharmaStpFile/table/mapPharmaStpFileExtendedRowFields";
import { getCmmStatuS } from "../../pharmaStpFile/table/utils/getCorrectCmmStatus";
import { getStpFileStatus } from "../../pharmaStpFile/table/utils/getCorrectStatus";
import getPoc from "../../pharmaStpFile/table/utils/getPoc";
import { getSftpBadgeStyles } from "../../pharmaStpFile/table/utils/getSftpBadgeStyles";
import {
  getBgColorForStpStatus,
  getTextColorForStpStatus,
} from "../../pharmaStpFile/table/utils/getStpStatusColors";
import { getBiosimilarBadgeButtonValue } from "./getBiosimilarBadgeButtonValue";
import { getSendToAppealBadgeButtonValue } from "./getSendToAppealBadgeButtonValue";
import {
  formatDobToDisplay,
  formatTimestampToDisplay,
} from "./utils/formatDobToDisplay";
import {
  getPaStatusBucketBadgeStyles,
  resolveDisplayPaOutcomeClassification,
} from "./utils/getPaStatusBucketBadgeStyles";
import { getRouteViaBadgeStyles } from "./utils/getRouteViaBadgeStyles";

/**
 * Column headers for the oncology submission orders table.
 * Order and labels align with the STP file table where applicable.
 */
export const OncologySubmissionTableHeader: TableHeader[] = [
  {
    label: "",
    key: OncologySubmissionTableColumnKeys.BATCH,
    order: 1,
    width: 4,
    sortable: false,
    type: TableCellType.CHECKBOX,
  },
  {
    label: "Case ID",
    key: OncologySubmissionTableColumnKeys.VIEW,
    order: 2,
    width: 7,
    sortable: false,
    type: TableCellType.STRING,
  },
  {
    label: "DOW",
    key: OncologySubmissionTableColumnKeys.DATE_OF_WORK,
    order: 3,
    width: 10,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "Patient Details",
    key: OncologySubmissionTableColumnKeys.PATIENT_NAME,
    order: 4,
    width: 12,
    sortable: false,
    type: TableCellType.MULTILINE,
  },
  {
    label: "Insurance",
    key: OncologySubmissionTableColumnKeys.INSURANCE_ID,
    order: 5,
    width: 8,
    sortable: false,
    type: TableCellType.STRING,
  },
  {
    label: "Drug",
    key: OncologySubmissionTableColumnKeys.DRUG,
    order: 6,
    width: 12,
    sortable: false,
    type: TableCellType.STRING,
  },
  {
    label: "Ref ID",
    key: OncologySubmissionTableColumnKeys.CMM_ID,
    order: 7,
    width: 8,
    sortable: false,
    type: TableCellType.STRING,
  },
  {
    label: "Route Via",
    key: OncologySubmissionTableColumnKeys.ROUTE_VIA,
    order: 8,
    width: 8,
    sortable: false,
    type: TableCellType.BADGE,
  },
  {
    label: "Second STP Status",
    key: OncologySubmissionTableColumnKeys.SECOND_STP_STATUS,
    order: 9,
    width: 10,
    sortable: false,
    type: TableCellType.BADGE,
  },
  {
    label: "Response Status",
    key: OncologySubmissionTableColumnKeys.RESPONSE_STATUS,
    order: 10,
    width: 10,
    sortable: false,
    type: TableCellType.BADGE,
  },
  {
    label: "Outcome Class",
    key: OncologySubmissionTableColumnKeys.PA_STATUS_BUCKET,
    order: 11,
    width: 10,
    sortable: false,
    type: TableCellType.BADGE,
  },
  {
    label: "Biosimilar",
    key: OncologySubmissionTableColumnKeys.BIOSIMILAR_CASE,
    order: 12,
    width: 10,
    sortable: false,
    type: TableCellType.BADGE_BUTTON,
  },
  {
    label: "Add",
    key: OncologySubmissionTableColumnKeys.ADD,
    order: 13,
    width: 5,
    sortable: false,
    type: TableCellType.ADD_KEY_BUTTON,
  },
  {
    label: "CMM",
    key: OncologySubmissionTableColumnKeys.CMM,
    order: 14,
    width: 7,
    sortable: false,
    type: TableCellType.BADGE_BUTTON,
  },
  {
    label: "Send to Appeal",
    key: OncologySubmissionTableColumnKeys.SEND_TO_APPEAL,
    order: 13,
    width: 7,
    sortable: false,
    type: TableCellType.BADGE_BUTTON,
  },
  // {
  //   label: "",
  //   key: OncologySubmissionTableColumnKeys.IDENTIFIER,
  //   order: 0,
  //   width: 3,
  //   sortable: false,
  //   type: TableCellType.COPY_ICON,
  // },
];

/**
 * Maps raw oncology submission API rows into custom-table cell values.
 *
 * @input data - Pharma STP / oncology submission records from the API
 * @return OncologySubmissionTableDataModel[]: Rows ready for CustomTable
 */
export const generateTableDataForOncologySubmission = (
  data: PharmaStpFileModel[],
): OncologySubmissionTableDataModel[] => {
  return data.map((item) => {
    const fileName: string = item?.filename ?? "";
    const dow: string = formatTimestampToDisplay(item?.dumped_at);
    const identifier: string = item?.identifier ?? "";

    // Checkbox data for batch column (empty label to show only checkbox)
    const batch = {
      id: identifier,
      label: "",
      isChecked: false,
      disabled: false,
    };
    const patient_mrn: string = item?.patient_mrn ?? "";
    const patientName: TableCellMultiline = {
      mainText: item?.patient_name ?? "",
      secondaryText: `DOB : ${formatDobToDisplay(item?.dob)}`,
      hideCopyIcon: true,
    };

    const sequence: string =
      String(item?.sequence ?? item?.seq ?? "").trim() || "";

    const routeVia: TableCellWithBadge = getRouteViaBadgeStyles(
      item?.route_via,
    );
    const drug: string = item?.drug ?? "";

    const cmmIdValue = item?.covermymed_id;
    const cmmId: string =
      !cmmIdValue || cmmIdValue === "nan" || cmmIdValue === "NaN"
        ? ""
        : String(cmmIdValue);

    const poc: string = getPoc(item?.poc ?? "");

    const isAddtext = item?.covermymed_id ? "Edit" : "Add";
    const add = {
      value: isAddtext,
      isDisabled: false,
      title: isAddtext,
    };

    const sftpStyles = getSftpBadgeStyles(item?.sftp_status);
    const sftp: TableCellWithBadge = {
      text: sftpStyles.title,
      color: sftpStyles.textColor,
      bgColor: sftpStyles.bgColor,
      hoverText: sftpStyles.title,
    };

    const outcomeClassValue = resolveDisplayPaOutcomeClassification(
      item?.response_status,
      item?.pa_outcome_classification,
    );

    const paStatusBucketStyles =
      getPaStatusBucketBadgeStyles(outcomeClassValue);
    const paStatusBucket: TableCellWithBadge = {
      text: paStatusBucketStyles.text,
      color: paStatusBucketStyles.textColor,
      bgColor: paStatusBucketStyles.bgColor,
      hoverText: paStatusBucketStyles.hoverText,
    };

    const paStatusReason: string = outcomeClassValue ?? "--";

    const secondStpStatusBadge: TableCellWithBadge = item?.second_stp_status
      ? {
          text:
            SecondStpStatusLabels[item.second_stp_status as SecondStpStatus] ??
            item.second_stp_status,
          color:
            getTextColorForStpStatus(item.second_stp_status as CmmStpStatus) ??
            "#111111",
          bgColor:
            getBgColorForStpStatus(item.second_stp_status as CmmStpStatus) ??
            "#F5F5F5",
        }
      : {
          text: "Not Sent",
          color: "#111111",
          bgColor: "#F5F5F5",
        };

    const responseStpStatusBadge: TableCellWithBadge = item?.response_status
      ? getStpFileStatus(item.response_status)
      : {
          text: item?.response_status || "--",
          color: "#0F0F0F",
          bgColor: "#F5F5F5",
        };

    const insuranceId: string = item?.pharmacy ?? "";
    const cmm: TableCellWithBadge = getCmmStatuS(
      item.meta?.cmm_st_rpa_status ?? "",
    );
    const sendToAppeal: TableCellWithBadge = getSendToAppealBadgeButtonValue({
      responseStatus: item.response_status ?? null,
      appealStatus: item.appeal_status ?? null,
    });

    const binValue = item?.bin;
    const bin: string =
      !binValue || binValue === "nan" || binValue === "NaN"
        ? ""
        : String(binValue);

    const extendedFields = mapPharmaStpFileExtendedRowFields(item);

    const biosimilarCase: TableCellWithBadge = getBiosimilarBadgeButtonValue({
      isBiosimilarCase: item?.is_biosimilar_case,
      parentIdentifier: item?.parent_identifier,
      sourceDrug: item?.source_drug,
    });

    return {
      fileName,
      sequence,
      batch,
      dow,
      patient_mrn,
      patientName,
      routeVia,
      drug,
      cmm,
      sendToAppeal,
      bin,
      insuranceId,
      paStatusBucket,
      cmmId,
      secondStpStatus: secondStpStatusBadge,
      responseStpStatus: responseStpStatusBadge,
      add,
      poc,
      sftp,
      identifier,
      textNoteStatus: item.text_note_status,
      cmmStatus: item.meta?.cmm_st_rpa_status,
      paStatusReason: paStatusReason,
      view: sequence || "--",
      routeViaValue: item?.route_via ?? "",
      responseStatusValue: item?.response_status ?? "",
      patient_name: item.patient_name ?? "",
      dob: item.dob ?? "",
      seq: item.seq ?? "",
      pharmacy_type: item.pharmacy_type ?? "",
      provider_name: item.provider_name ?? "",
      insuranceid: item.insuranceid ?? null,
      pharmacy: item.pharmacy ?? null,
      covermymed_id: item.covermymed_id ?? null,
      filename: item.filename ?? "",
      org_id: item.org_id ?? "",
      dumped_at: item.dumped_at ?? "",
      meta: item.meta,
      rx_due_date: item.rx_due_date ?? null,
      pharmacy_phone: item.pharmacy_phone ?? null,
      sftp_status: item.sftp_status ?? null,
      second_stp_status: item.second_stp_status ?? null,
      response_status: item.response_status ?? null,
      route_via: item.route_via ?? null,
      pa_status_reason: item.pa_status_reason ?? null,
      comments: item.comments ?? null,
      parent_identifier: item.parent_identifier ?? null,
      source_drug: item.source_drug ?? null,
      is_biosimilar_case: item.is_biosimilar_case ?? null,
      ...extendedFields,
      biosimilarCase: biosimilarCase,
    };
  });
};
